import { useEffect, useState } from 'react';

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export interface UseAnimatedMetricOptions {
  durationMs?: number;
  start?: number;
  delayMs?: number;
  /** When false, holds at `start` until enabled (e.g. scroll-into-view). */
  enabled?: boolean;
}

/**
 * Animates a numeric value from `start` to `target` with ease-out cubic.
 */
export function useAnimatedMetric(
  target: number,
  options: UseAnimatedMetricOptions = {},
): number {
  const {
    durationMs = 720,
    start: startValue = 0,
    delayMs = 60,
    enabled = true,
  } = options;

  const [value, setValue] = useState(startValue);

  useEffect(() => {
    if (!enabled) {
      setValue(startValue);
      return;
    }

    if (prefersReducedMotion()) {
      setValue(target);
      return;
    }

    setValue(startValue);
    let raf = 0;
    const timeout = window.setTimeout(() => {
      let startTime: number | null = null;
      const from = startValue;
      const to = target;

      const tick = (now: number) => {
        if (startTime === null) startTime = now;
        const t = Math.min(1, (now - startTime) / durationMs);
        setValue(from + (to - from) * easeOutCubic(t));
        if (t < 1) raf = requestAnimationFrame(tick);
      };

      raf = requestAnimationFrame(tick);
    }, delayMs);

    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(raf);
    };
  }, [target, startValue, durationMs, delayMs, enabled]);

  return value;
}
